import { create } from 'zustand';
import type { Board, BoardShare } from './canvasStore';

export type ViewMode = 'discover' | 'focus';

// Get stored view mode with migration from old values
const getStoredViewMode = (): ViewMode => {
  const stored = localStorage.getItem('viewMode');
  if (stored === 'wall') return 'discover';
  if (stored === 'canvas' || stored === 'boards' || stored === 'focus') return 'focus';
  if (stored === 'discover') return 'discover';
  return 'focus';
};

export interface BoardState {
  boards: Board[];
  currentBoardId: string | null;
  boardPath: Board[];
  boardShares: BoardShare[];
  viewMode: ViewMode;

  // Actions
  setBoards: (boards: Board[]) => void;
  setCurrentBoardId: (id: string | null) => void;
  setBoardPath: (path: Board[]) => void;
  setViewMode: (mode: ViewMode) => void;
  updateBoardInList: (id: string, updates: Partial<Board>) => void;
  removeBoardFromList: (id: string) => void;
  addBoard: (board: Board) => void;
  setBoardShares: (shares: BoardShare[]) => void;
}

export const useBoardStore = create<BoardState>((set, get) => ({
  boards: [],
  currentBoardId: null,
  boardPath: [],
  boardShares: [],
  viewMode: getStoredViewMode(),

  setBoards: (boards) => set({ boards }),
  setCurrentBoardId: (id) => set({ currentBoardId: id }),
  setBoardPath: (path) => set({ boardPath: path }),

  setViewMode: (mode) => {
    localStorage.setItem('viewMode', mode);
    set({ viewMode: mode });
    // Update URL
    const url = new URL(window.location.href);
    if (mode === 'focus') {
      url.searchParams.delete('view');
    } else {
      url.searchParams.set('view', mode);
    }
    window.history.pushState({}, '', url.toString());
  },

  updateBoardInList: (id, updates) => set((state) => ({
    boards: state.boards.map(b => b.id === id ? { ...b, ...updates } : b),
  })),

  removeBoardFromList: (id) => set((state) => ({
    boards: state.boards.filter(b => b.id !== id),
  })),

  addBoard: (board) => set((state) => ({
    boards: [...state.boards, board],
  })),

  setBoardShares: (shares) => set({ boardShares: shares }),
}));
